/**
 * KycSelect - Web端表单选择器组件
 *
 * @description KYC表单专用的选择器组件，Web端实现，集成CommonSelect和表单布局功能
 * @features
 * - 基于Ant Design Select实现
 * - 集成AsFormItemLayout，提供完整的表单项布局
 * - 支持表单验证和错误提示显示
 *
 * @usage
 * ```tsx
 * // 基础表单用法
 * <KycSelect
 *   value="CN"
 *   label="选择国家"
 *   placeholder="请选择国家"
 *   options={[
 *     {value: 'CN', title: '中国'},
 *     {value: 'US', title: '美国'}
 *   ]}
 *   onValueChange={(value) => console.log('选中:', value)}
 * />
 * ```
 *
 * @note 此组件专为KYC表单设计，集成了完整的表单验证和布局功能，适用于各种表单场景
 */

import React, { useCallback } from "react";
import type { IAsSelectProps } from "@adgm/cross-models";
import { CommonSelect } from "../../common";
import { AsFormItemLayout } from "../AsFormItemLayout";
import { i18nCrossUtil } from "@adgm/libs";
import { KycLabelElement } from "../KycLabelElement";
import { View } from "../../platformComponents";
import { padding } from "../../styles/common";
import { useGenericFormContext } from "../GenericForm";
const crossIntl = i18nCrossUtil.t();
/**
 * KycSelect组件主实现
 *
 * @description KYC表单选择器的Web端实现，集成表单布局和选择器功能
 * @param props - 组件属性，详见IAsSelectProps接口
 * @returns React函数组件
 */
export const KycSelect = React.memo((props: IAsSelectProps) => {
  const {
    style,
    label,
    error,
    tip,
    hideErrorSpace,
    value,
    placeholder = crossIntl["kyc.please.select"],
    onValueChange,
    options,
    filterOption,
    showSearch,
    disabled = false,
    multiple,
    sequenceNumber,
    horizontalLayout,
    className,
    required,
    ...restProps
  } = props;
  const { genericFormType } = useGenericFormContext();
  const isQuestionnaireForm = genericFormType === "questionnaire";
  /**
   * 处理选项选择事件
   * @description 用户选择选项时触发，使用useCallback优化性能
   * @param selectedValue - 用户选中的选项值
   */
  const handleSelect = useCallback(
    (selectedValue: string) => {
      onValueChange?.(selectedValue);
    },
    [onValueChange],
  );

  return (
    <AsFormItemLayout
      label={label}
      error={error}
      tip={tip}
      required={required}
      hideErrorSpace={hideErrorSpace}
      horizontalLayout={horizontalLayout}
      labelElement={
        <KycLabelElement
          label={label}
          sequenceNumber={sequenceNumber}
          horizontalLayout={horizontalLayout}
          required={required}
        />
      }
      {...restProps}>
      <View style={[{ flex: 1, paddingLeft: isQuestionnaireForm ? padding.p40 : 0 }, style]}>
        <CommonSelect
          className={className}
          // 这里需要undefined 因为默认数据/后端返回的数据可能是空字符串。这样Select就会存在空显示
          value={value || undefined}
          multiple={multiple}
          placeholder={placeholder}
          options={options}
          onSelect={handleSelect}
          filterOption={filterOption}
          showSearch={showSearch}
          disabled={disabled}
        />
      </View>
    </AsFormItemLayout>
  );
});
